import { ApiErrorCode, ApiResponse, withApiErrorHandler } from '@/modules/common/api/errors';
import { axiosInstance } from '@/utils/axios';

import { mapUserGroupPermissionsFromResponse } from '../mappers/permission.mapper';
import { UserGroupPermissions } from '../types/permission';
import {
  CreateUserGroupRequest,
  DeleteUserGroupRequest,
  RenameUserGroupRequest,
} from '../types/request/user-group-request';
import { UserGroupPoliciesResponse } from '../types/response/permission-response';

const USER_GROUPS_ENDPOINT = '/api/opsium-user-groups';

/**
 * Creates a user group and returns it, with the policies that were actually applied — the backend
 * skips unknown policy names rather than rejecting them.
 *
 * Callers still refetch the permissions matrix; this response describes only the new group.
 */
export const createUserGroup = async (
  request: CreateUserGroupRequest
): Promise<UserGroupPermissions> => {
  const response = await withApiErrorHandler(
    USER_GROUPS_ENDPOINT,
    ApiErrorCode.USER_GROUP_CREATE_FAILED,
    () => axiosInstance.post<ApiResponse<UserGroupPoliciesResponse>>(USER_GROUPS_ENDPOINT, request)
  );

  return mapUserGroupPermissionsFromResponse(response);
};

export const renameUserGroup = async ({
  id,
  name,
}: { id: number } & RenameUserGroupRequest): Promise<void> => {
  const endpoint = `${USER_GROUPS_ENDPOINT}/${id}`;

  const requestBody: RenameUserGroupRequest = { name };

  await withApiErrorHandler(endpoint, ApiErrorCode.USER_GROUP_RENAME_FAILED, () =>
    axiosInstance.patch<ApiResponse<never>>(endpoint, requestBody)
  );
};

/**
 * Makes a user group the organization's default — where members invited without an explicit group
 * are placed. The backend clears the marker from the previous default in the same transaction.
 */
export const setDefaultUserGroup = async ({ id }: { id: number }): Promise<void> => {
  const endpoint = `${USER_GROUPS_ENDPOINT}/${id}/default`;

  await withApiErrorHandler(endpoint, ApiErrorCode.USER_GROUP_SET_DEFAULT_FAILED, () =>
    axiosInstance.patch<ApiResponse<never>>(endpoint)
  );
};

/**
 * Deletes a user group and moves its members to the replacement group.
 * The request carries a body, hence the axios `{ data }` config.
 */
export const deleteUserGroup = async ({
  id,
  replacementGroupId,
}: { id: number } & DeleteUserGroupRequest): Promise<void> => {
  const endpoint = `${USER_GROUPS_ENDPOINT}/${id}`;

  const requestBody: DeleteUserGroupRequest = { replacementGroupId };

  await withApiErrorHandler(endpoint, ApiErrorCode.USER_GROUP_DELETE_FAILED, () =>
    axiosInstance.delete<ApiResponse<never>>(endpoint, { data: requestBody })
  );
};
